import React, { useCallback, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import TopBar from './TopBar';
import SessionWarning from './SessionWarning';
import { useNavigation } from '../contexts/NavigationContext';
import { useAuth } from '../contexts/AuthContext';
import { showSessionExpiredNotification } from '../utils/toast-helper';

const Layout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { setShowNavigationWarning, setPendingNavigation } = useNavigation();
  const { signOut } = useAuth();

  // Check token validity periodically
  useEffect(() => {
    const checkTokenValidity = () => {
      try {
        const idToken = localStorage.getItem('idToken');
        
        if (!idToken) {
          return;
        }
        
        // Decode token to check expiration
        const idTokenPayload = JSON.parse(atob(idToken.split('.')[1]));
        const currentTime = Math.floor(Date.now() / 1000);
        
        if (idTokenPayload.exp < currentTime) {
          console.log('Token expired in Layout');
          
          // Show notification and redirect to login
          showSessionExpiredNotification();
          
          // Sign out and redirect
          signOut().then(() => {
            window.location.href = '/login';
          });
        }
      } catch (error) {
        console.error('Error checking token validity in Layout:', error);
      }
    };
    
    // Check token validity on mount and every minute
    checkTokenValidity();
    const intervalId = setInterval(checkTokenValidity, 60000);
    
    // Check on window focus
    const handleFocus = () => {
      checkTokenValidity();
    };
    
    window.addEventListener('focus', handleFocus);
    
    return () => {
      clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
    };
  }, [signOut]);

  const handleNavigationGuard = useCallback((path: string) => {
    console.log('Navigation guard check for path:', path);
    const isEditPage = location.pathname.includes('/edit/');
    const editComponent = document.querySelector('[data-has-unsaved-changes="true"]');
    
    if (isEditPage && editComponent) {
      console.log('Unsaved changes detected, showing warning');
      setShowNavigationWarning(true);
      setPendingNavigation(path);
      return false;
    }
    
    return true;
  }, [location.pathname, setShowNavigationWarning, setPendingNavigation]);

  const handleLogout = async () => {
    console.log('Layout: handleLogout called');
    try {
      // First check for unsaved changes
      const isEditPage = location.pathname.includes('/edit/');
      const editComponent = document.querySelector('[data-has-unsaved-changes="true"]');
      
      if (isEditPage && editComponent) {
        console.log('Unsaved changes detected during logout');
        setShowNavigationWarning(true);
        setPendingNavigation('/login');
        return;
      }

      console.log('Calling signOut');
      await signOut();
      
      console.log('Navigating to login page');
      
      // Force a hard redirect to the login page
      window.location.href = '/login';
      
      // Backup navigation using react-router (in case the hard redirect fails)
      setTimeout(() => {
        console.log('Attempting backup navigation');
        navigate('/login', { replace: true });
      }, 100);
    } catch (error) {
      console.error('Logout error:', error);
      // Force navigation even if there's an error
      window.location.href = '/login';
    }
  };

  return (
    <div className="flex flex-col h-screen">
      <TopBar />
      <main className="flex-1 overflow-auto pt-16 md:pt-[4.5rem]">
        <Outlet />
      </main>
      
      {/* Add the session warning component */}
      <SessionWarning />
    </div>
  );
};

export default Layout;